import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, Trash, Eye, Link as LinkIcon, WhatsappLogo, QrCode, Pencil } from "@phosphor-icons/react";
import { api, getUser } from "@/lib/api";
import { publicUrl } from "@/lib/publicUrl";
import Paginator from "@/components/Paginator";
import { VideoPagesSection } from "@/components/videobuy/VideoPagesSection";

const buildRefLink = (slug, code) =>
  publicUrl(`/f/${slug}${code ? `?ref=${code}` : ""}`);

const ShareRow = ({ slug, code, id }) => {
  const [showQr, setShowQr] = useState(false);
  const link = buildRefLink(slug, code);
  const wapp = `https://wa.me/?text=${encodeURIComponent(
    `Confere isso: ${link}`,
  )}`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(link)}`;
  return (
    <div className="mt-3 p-3 rounded-sm border border-[var(--accent)]/30 bg-[var(--accent)]/[0.03]">
      <div className="label-caps text-[10px] text-[var(--accent)]">Seu link com rastreio</div>
      <div className="mt-1 font-mono text-[11px] break-all text-white/80" data-testid={`ref-link-${id}`}>
        {link}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          onClick={() => {
            navigator.clipboard.writeText(link);
            toast.success("Link copiado com rastreio de afiliação");
          }}
          className="btn-ghost text-xs flex items-center gap-1"
          data-testid={`copy-ref-${id}`}
        >
          <Copy size={14} /> Copiar
        </button>
        <a
          href={wapp}
          target="_blank"
          rel="noreferrer"
          className="btn-ghost text-xs flex items-center gap-1"
          data-testid={`wapp-share-${id}`}
        >
          <WhatsappLogo size={14} /> WhatsApp
        </a>
        <button
          onClick={() => setShowQr((v) => !v)}
          className="btn-ghost text-xs flex items-center gap-1"
          data-testid={`qr-toggle-${id}`}
        >
          <QrCode size={14} /> QR Code
        </button>
      </div>
      {showQr && (
        <div className="mt-3 flex justify-center bg-white p-3 rounded-sm">
          <img src={qrSrc} alt="QR Code do link" width={200} height={200} data-testid={`qr-img-${id}`} />
        </div>
      )}
    </div>
  );
};

export default function SalesLinks() {
  const [funnels, setFunnels] = useState([]);
  const [pg, setPg] = useState({ page: 1, pages: 1, total: 0 });
  const [affCode, setAffCode] = useState("");
  const user = getUser();

  const load = async (p = 1) => {
    const { data } = await api.get(`/funnels?page=${p}&per_page=10`);
    setFunnels(data.funnels || []);
    setPg({ page: data.page, pages: data.pages, total: data.total });
  };

  useEffect(() => {
    load(1);
    // Busca o affiliate_code atualizado (backfill garante existir)
    api.get("/account/affiliate").then(({ data }) => setAffCode(data.affiliate_code || "")).catch(() => {
      setAffCode(user?.affiliate_code || "");
    });
  }, [user?.affiliate_code]);

  const del = async (id) => {
    if (!window.confirm("Excluir este funil?")) return;
    await api.delete(`/funnels/${id}`);
    toast.success("Funil removido");
    load(pg.page);
  };

  const copyLink = (slug) => {
    const url = publicUrl(`/f/${slug}`);
    navigator.clipboard.writeText(url);
    toast.success("Link copiado: " + url);
  };

  return (
    <div className="fadeUp">
      <div className="flex justify-between items-baseline mb-10">
        <div>
          <div className="label-caps mb-2">Captação</div>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Links de venda</h1>
          <p className="text-white/60 mt-2">Vídeo + botão próximo + captura de leads + entrega automática.</p>
        </div>
        <Link to="/app/funnels/new" className="btn-primary flex items-center gap-2" data-testid="new-funnel-btn">
          <Plus size={18} weight="bold" /> Novo funil
        </Link>
      </div>

      {funnels.length === 0 ? (
        <div className="card-flat p-16 text-center">
          <LinkIcon size={48} color="var(--accent)" weight="duotone" className="mx-auto" />
          <div className="font-display font-bold text-2xl mt-4">Nenhum funil ainda</div>
          <div className="text-white/60 mt-2">Crie seu primeiro link de vendas em 30 segundos.</div>
          <Link to="/app/funnels/new" className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="empty-new-funnel">
            <Plus size={18} weight="bold" /> Criar funil
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {funnels.map((f) => (
            <div key={f.id} className="card-flat p-6 min-w-0" data-testid={`funnel-${f.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-display font-bold text-xl">{f.title}</div>
                  <div className="text-xs text-white/50 font-mono mt-1">/f/{f.slug}</div>
                </div>
                <div className="label-caps flex items-center gap-1"><Eye size={12} /> {f.views}</div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2 text-xs">
                <div className="px-2 py-1 border border-[var(--border)] rounded-sm">{f.plans?.length || 0} planos</div>
                {f.file_url && <div className="px-2 py-1 border border-[var(--border)] rounded-sm">Entrega ativa</div>}
                {f.direct_buy_enabled && <div className="px-2 py-1 border border-[var(--accent)]/50 text-[var(--accent)] rounded-sm" data-testid={`funnel-direct-badge-${f.id}`}>Vídeo + Compra direta</div>}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => copyLink(f.slug)} className="btn-ghost text-xs flex items-center gap-1" data-testid={`copy-${f.id}`}>
                  <Copy size={14} /> Copiar link
                </button>
                <a href={`/f/${f.slug}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs flex items-center gap-1">
                  <Eye size={14} /> Ver página
                </a>
                <Link to={`/app/funnels/${f.id}/edit`} className="btn-ghost text-xs flex items-center gap-1 text-[var(--accent)]" data-testid={`edit-${f.id}`}>
                  <Pencil size={14} /> Editar
                </Link>
                <button onClick={() => del(f.id)} className="btn-ghost text-xs text-[var(--danger)] flex items-center gap-1 ml-auto" data-testid={`del-${f.id}`}>
                  <Trash size={14} />
                </button>
              </div>
              <ShareRow slug={f.slug} code={affCode} id={f.id} />
            </div>
          ))}
        </div>
      )}
      <div className="mt-4"><Paginator page={pg.page} pages={pg.pages} onChange={load} total={pg.total} /></div>
      <VideoPagesSection />
    </div>
  );
}
